/**
 * /join page — pure helpers (no React, no network) so they are unit-testable.
 */

export interface JoinForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  acceptTerms: boolean;
}

export const PASSWORD_MIN_LENGTH = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** XXXX-XXXX over ABCDEFGHJKLMNPQRSTUVWXYZ23456789 — same rule as the backend. */
const CODE_RE = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;

export const EMPTY_FORM: JoinForm = {
  firstName: '', lastName: '', email: '', password: '', acceptTerms: false,
};

/** Read ?code= from the URL. Returns the normalized code, or null when absent/malformed. */
export function getCodeFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get('code');
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return CODE_RE.test(code) ? code : null;
}

/** Client-side mirror of the backend validation. Returns offending field names. */
export function validateJoinForm(f: JoinForm): Array<keyof JoinForm> {
  const bad: Array<keyof JoinForm> = [];
  if (f.firstName.trim().length === 0 || f.firstName.trim().length > 80) bad.push('firstName');
  if (f.lastName.trim().length === 0 || f.lastName.trim().length > 80) bad.push('lastName');
  if (!EMAIL_RE.test(f.email.trim())) bad.push('email');
  if (f.password.length < PASSWORD_MIN_LENGTH) bad.push('password');
  if (f.acceptTerms !== true) bad.push('acceptTerms');
  return bad;
}

export interface JoinApiError {
  status: number;
  details?: { reason?: string; fields?: string[] } | null;
}

/** Map a backend/HTTP failure to an i18n key the page can show. */
export function mapJoinError(err: JoinApiError): string {
  const reason = err.details?.reason;
  switch (err.status) {
    case 404: return 'join.err.notFound';
    case 410:
      if (reason === 'expired') return 'join.err.expired';
      if (reason === 'exhausted') return 'join.err.exhausted';
      return 'join.err.inactive';
    case 409: return 'join.err.emailExists';
    case 400:
      if (reason === 'weak_password') return 'join.err.weakPassword';
      if (reason === 'otp_required') return 'join.err.otpRequired';
      if (reason === 'otp_invalid') return 'join.err.otpInvalid';
      if (reason === 'otp_expired') return 'join.err.otpExpired';
      if (reason === 'otp_locked') return 'join.err.otpLocked';
      return 'join.err.invalid';
    case 429:
      if (reason === 'velocity') return 'join.err.velocity';
      if (reason === 'otp_rate') return 'join.err.otpRate';
      return 'join.err.rateLimited';
    case 502: return reason === 'mail_error' ? 'join.err.mailError' : 'join.err.server';
    default: return 'join.err.server';
  }
}
